<script>
import {defineComponent, onMounted, computed, ref} from 'vue'
import {useCompareStore} from "@js/store/CompareStore.js"
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue"
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue"
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue"
import {Link} from "@inertiajs/vue3"
import IconTrash from "@js/components/Icons/IconTrash.vue"
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue"
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue"

export default defineComponent({
    name: "CompareProductsPage",
    components: {
        OnlyMobile,
        OnlyDesktop,
        IconTrash,
        Link,
        NumberFormatComponent,
        MobileTopBar,
        Breadcrumbs
    },
    setup() {
        const compareStore = useCompareStore()
        const showOnlyDiff = ref(false)

        onMounted(() => {
            compareStore.getCompares()
            compareStore.getComparedProductsData()
        })

        const products = computed(() => {
            const data = compareStore.comparing_data?.data || {}
            // Порядок как в cookie (важно для UX)
            const order = compareStore.comparedIds || []
            const byId = Object.values(data)
            if (!order.length) return byId
            const map = new Map(byId.map(p => [p.id, p]))
            return order.map(id => map.get(id)).filter(Boolean)
        })

        const characteristics = computed(() => {
            const charsObj = compareStore.comparing_data?.characteristics || {}
            // массив характеристик, сортируем по title
            return Object.values(charsObj).sort((a, b) => a.title.localeCompare(b.title, 'ru'))
        })

        // Вспомогалка: вернуть значение характеристики у товара
        const valueOf = (product, charTypeId) => {
            return product?.char_data?.[charTypeId]?.value ?? '—'
        }

        // Ряды фиксированных метрик (до характеристик)
        const fixedRows = computed(() => ([
            {
                key: 'price',
                title: 'Цена',
                render: (p) => p.price != null
                    ? {type: 'price', value: p.price, append: ' ₽'}
                    : {type: 'text', value: '—'}
            },
        ]))

        // Проверка «все значения одинаковые в строке»
        const isRowDifferent = (getterFn) => {
            const vals = products.value.map(getterFn)
            if (!vals.length) return false
            const norm = (v) => (typeof v === 'object' && v !== null) ? JSON.stringify(v) : String(v)
            const first = norm(vals[0])
            return vals.some(v => norm(v) !== first)
        }

        const dynamicRows = computed(() => {
            // каждая характеристика -> генератор для получения значения по продукту
            return characteristics.value.map(ch => ({
                key: `char_${ch.product_characteristic_type_id}`,
                title: ch.title,
                render: (p) => ({type: 'text', value: valueOf(p, ch.product_characteristic_type_id)}),
                isDifferent: isRowDifferent((p) => valueOf(p, ch.product_characteristic_type_id)),
            }))
        })

        const allRows = computed(() => {
            const fixed = fixedRows.value.map(r => ({
                ...r,
                isDifferent: isRowDifferent((p) => r.render(p).value),
            }))
            return [...fixed, ...dynamicRows.value]
        })

        const rowsToShow = computed(() => {
            if (!showOnlyDiff.value) return allRows.value
            return allRows.value.filter(r => r.isDifferent)
        })

        const remove = (id) => {
            compareStore.toggleCompare(id)
            compareStore.getCompares()
            compareStore.getComparedProductsData()
        }

        return {
            compareStore,
            products,
            rowsToShow,
            showOnlyDiff,
            remove,
        }
    }
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>
        <div class="flex max-md:flex-col md:items-center justify-between mb-6 gap-5">
            <h1 class="text-2xl font-bold">Сравнение товаров</h1>
            <div v-if="products.length" class="flex items-center">
                <input v-model="showOnlyDiff" id="checked-checkbox" type="checkbox" value=""
                       class="w-4 h-4 text-baseColor bg-gray-100 border-gray-300 rounded-sm focus:ring-0">
                <label for="checked-checkbox" class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                    Показать только различия
                </label>
            </div>
        </div>
    </div>

    <div class="sm:container">
        <!-- Skeleton -->
        <div v-if="compareStore.loading_products" class="flex animate-pulse space-x-4 p-4">
            <div class="flex-1 overflow-auto">
                <div class="flex space-x-4">
                    <OnlyDesktop :maxSize="1024">
                        <div v-for="n in 5" :key="'d'+n" class="w-64 space-y-4 border border-gray-200 p-4 rounded-lg">
                            <div class="h-24 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded" v-for="m in 10" :key="m"></div>
                        </div>
                    </OnlyDesktop>
                    <OnlyMobile :maxSize="1024">
                        <div v-for="n in 2" :key="'m'+n" class="w-64 space-y-4 border border-gray-200 p-4 rounded-lg">
                            <div class="h-24 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded" v-for="m in 10" :key="m"></div>
                        </div>
                    </OnlyMobile>
                </div>
            </div>
        </div>

        <!-- Пусто -->
        <div v-else-if="!products.length" class="italic text-gray-500">
            Нет товаров для сравнения.
        </div>

        <!-- Таблица сравнения -->
        <div v-else class="overflow-x-auto md:border border-gray-200 md:rounded-lg">
            <table class="table-fixed min-w-max border-collapse">
                <thead>
                <tr>
                    <!-- Фиксированная пустая ячейка слева -->
                    <th class="sticky left-0 bg-white z-1 px-4 py-2 text-left">Характеристика</th>

                    <th v-for="p in products" :key="p.id"
                        class="relative px-4 py-3 text-center border-l border-gray-200 w-64 align-top">
                        <button @click.prevent="remove(p.id)"
                                class="absolute top-2 right-2 text-gray-400 md:hover:text-white p-1 border border-gray-400 md:hover:border-red-600 md:hover:bg-red-600 rounded-full transition">
                            <IconTrash class="w-5 h-5"/>
                        </button>

                        <Link :href="route('product', {slug: p.slug})" class="group block">
                            <img :src="p.image" alt="" class="h-24 w-24 mx-auto mb-2 object-contain rounded"/>
                            <div class="max-md:text-sm font-semibold truncate group-hover:text-navHoverColor transition">
                                {{ p.title }}
                            </div>
                        </Link>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in rowsToShow" :key="row.key" class="border-t bg-gray-50">
                    <th class="max-md:text-xs text-sm sticky left-0 z-1 px-4 py-2 text-left font-medium bg-gray-50">
                        {{ row.title }}
                    </th>
                    <td v-for="p in products" :key="p.id + '_' + row.key"
                        class="max-md:text-xs text-sm px-4 py-2 text-center border-l border-gray-200"
                        :class="row.isDifferent ? 'bg-amber-50' : ''">
                        <template v-if="row.render(p).type === 'price'">
                            <NumberFormatComponent :number="row.render(p).value" :append_text="row.render(p).append"/>
                        </template>
                        <template v-else>
                            {{ row.render(p).value }}
                        </template>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped lang="scss">
.z-1 {
    z-index: 1;
}

table {
    width: max-content;
}

th, td {
    vertical-align: middle;
}
</style>
